import React, { useState, useEffect } from 'react';
import { getDBSuggestions, getFastLLMSuggestions, getSmartSuggestions } from '../../../services/suggestion.service';
import './IdeaSuggester.css';

const IdeaSuggester = ({ value, onChange, onFocus, onBlur, placeholder, disabled }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingDB, setIsLoadingDB] = useState(false);
  const [dbSuggestionsTimer, setDbSuggestionsTimer] = useState(null);

  // Immediate DB-based suggestions (existing valid ideas)
  useEffect(() => {
    if (value.length >= 5 && !disabled) {
      // Clear previous timer
      if (dbSuggestionsTimer) {
        clearTimeout(dbSuggestionsTimer);
      }
      
      // Debounce DB search by 300ms
      const timer = setTimeout(async () => {
        setIsLoadingDB(true);
        try {
          // Step 1: Get DB suggestions first (existing reports) - highest priority
          const dbSuggs = await getDBSuggestions(value);
          if (dbSuggs && dbSuggs.length > 0) {
            setSuggestions(dbSuggs);
            setShowSuggestions(true);
          }
          
          // Step 2: Get fast LLM suggestions (no DB lookup, faster)
          getFastLLMSuggestions(value).then(fastSuggs => {
            if (fastSuggs && fastSuggs.length > 0) {
              setSuggestions(prev => {
                // Keep DB suggestions at the top, add LLM suggestions after
                const dbItems = prev.filter(s => s.type === 'db');
                const combined = [...dbItems, ...fastSuggs];
                const unique = Array.from(
                  new Map(combined.map(s => [s.text.toLowerCase(), s])).values()
                );
                return unique.slice(0, 8);
              });
              setShowSuggestions(true);
            }
          }).catch(err => {
            // Silent fail
          });
          
          // Step 3: Get hybrid suggestions in background (LLM + DB context, slower)
          getSmartSuggestions(value, dbSuggs).then(smartSuggs => {
            if (smartSuggs && smartSuggs.length > 0) {
              setSuggestions(prev => {
                // Preserve DB suggestions at top, merge LLM and hybrid suggestions
                const dbItems = prev.filter(s => s.type === 'db');
                const llmItems = prev.filter(s => s.type !== 'db');
                const combined = [...dbItems, ...llmItems, ...smartSuggs];
                const unique = Array.from(
                  new Map(combined.map(s => [s.text.toLowerCase(), s])).values()
                );
                return unique.slice(0, 8); // Limit to 8 total
              });
            }
          }).catch(err => {
            console.warn('[IdeaSuggester] Hybrid suggestions failed:', err);
          });
        } catch (error) {
          console.error('DB suggestions failed:', error);
        } finally {
          setIsLoadingDB(false);
        }
      }, 300);
      
      setDbSuggestionsTimer(timer);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (dbSuggestionsTimer) {
        clearTimeout(dbSuggestionsTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, disabled]);

  const handleSelectSuggestion = (suggestion) => {
    // For DB suggestions, replace the entire text with the suggested idea
    onChange(suggestion.text);
    setShowSuggestions(false);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
    if (onFocus) onFocus();
  };

  const handleInputBlur = () => {
    // Delay hiding to allow click on suggestion
    setTimeout(() => setShowSuggestions(false), 200);
    if (onBlur) onBlur();
  };

  return (
    <div className="idea-suggester">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        disabled={disabled}
        placeholder={placeholder || "e.g., Radar based sleep analysis (suggestions will appear as you type)"}
        className="idea-input-with-suggestions"
      />
      
      {showSuggestions && (
        <div className="suggestions-dropdown">
          <div className="suggestions-header">
            <span>💡 Idea Suggestions {suggestions.length > 0 && `(${suggestions.length})`}</span>
            <div className="suggestions-header-right">
              {isLoadingDB && <span className="loading-indicator">Loading...</span>}
              <button 
                className="close-suggestions-btn"
                onClick={() => setShowSuggestions(false)}
                onMouseDown={(e) => e.preventDefault()}
                title="Close suggestions"
              >
                ×
              </button>
            </div>
          </div>
          
          {suggestions.map((suggestion, idx) => (
            <div
              key={idx}
              className={`suggestion-item ${suggestion.type}`}
              onClick={() => handleSelectSuggestion(suggestion)}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
            >
              <span className="suggestion-icon">{suggestion.icon}</span>
              <div className="suggestion-content">
                <div className="suggestion-text">{suggestion.text}</div>
                {suggestion.reason && (
                  <div className="suggestion-reason">{suggestion.reason}</div>
                )}
              </div>
              {suggestion.confidence && (
                <span className="suggestion-confidence">
                  {Math.round(suggestion.confidence * 100)}%
                </span>
              )}
            </div>
          ))}
          
          {isLoadingDB && suggestions.length === 0 && (
            <div className="suggestion-item loading">
              <span className="spinner-small"></span>
              <span>Searching for suggestions...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IdeaSuggester;
